import { useEffect, useState, useCallback } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { apiClient } from '@api/client';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '@store/notificationStore';
import type { DocumentExportRequest, WorkflowStatus, DocumentProcessingStatus, PendingDocument } from '@schemas/api';

/**
 * Real-time workflow status via Server-Sent Events (SSE)
 * Provides live updates on document extraction and correlation progress
 */
export const useWorkflowStatusSSE = (workflowId: string | null) => {
  const [status, setStatus] = useState<WorkflowStatus | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!workflowId) return;

    setIsConnecting(true);
    let eventSource: EventSourcePolyfill | null = null;
    let reconnectTimeout: number | null = null;
    let shouldReconnect = true;

    const connectSSE = () => {
      try {
        const baseUrl = apiClient.defaults.baseURL || 'http://localhost:8000';
        const url = `${baseUrl}/workflows/${workflowId}/status/stream`;

        eventSource = new EventSourcePolyfill(url, {
          withCredentials: true,
        });

        eventSource.onopen = () => {
          setIsConnected(true);
          setIsConnecting(false);
          setError(null);
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            setStatus(data);
            if (data?.completed) {
              shouldReconnect = false;
              setIsConnected(false);
              setIsConnecting(false);
              setError(null);
              eventSource?.close();
            }
          } catch (e) {
            console.error('Failed to parse SSE message:', e);
          }
        };

        eventSource.onerror = (event) => {
          if (!shouldReconnect) {
            if (eventSource) {
              eventSource.close();
            }
            return;
          }

          console.error('SSE connection error:', event);
          setIsConnected(false);
          setError('Connection lost. Retrying...');

          // Try to reconnect after 3 seconds
          reconnectTimeout = window.setTimeout(() => {
            if (eventSource) {
              eventSource.close();
            }
            connectSSE();
          }, 3000);
        };
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to connect to workflow status stream';
        setError(errorMsg);
        setIsConnecting(false);
        console.error('SSE setup error:', err);
      }
    };

    connectSSE();

    return () => {
      shouldReconnect = false;
      if (reconnectTimeout) {
        window.clearTimeout(reconnectTimeout);
      }
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [workflowId]);

  return { status, isConnecting, isConnected, error };
};

/**
 * Poll document processing status
 * Useful as a fallback when SSE is not available
 */
export const useDocumentProcessingStatus = (documentId: string | null) => {
  return useQuery<DocumentProcessingStatus>({
    queryKey: ['document-processing-status', documentId],
    queryFn: async () => {
      if (!documentId) throw new Error('Document ID is required');
      const { data } = await apiClient.get<DocumentProcessingStatus>(
        `/documents/${documentId}/processing-status`
      );
      return data;
    },
    enabled: !!documentId,
    refetchInterval: 3000, // Poll every 3 seconds
    staleTime: 1000, // Consider data stale after 1 second
  });
};

/**
 * Query documents with filtering and pagination
 * Supports filtering by type, status, vendor, and more
 */
export interface DocumentFilters {
  document_type?: "purchase_order" | 'receipt' | 'invoice';
  extraction_status?: string;
  vendor_id?: string;
  search?: string;
  match_status?: string;
  correlation_id?: string;
  created_from?: string;
  created_to?: string;
  amount_min?: number;
  amount_max?: number;
  has_issues?: boolean;
  erp_connection_id?: string;
  sort_by?: 'created_at' | 'vendor_name' | 'amount' | 'confidence' | 'erp_link';
  sort_order?: 'asc' | 'desc';
  skip?: number;
  limit?: number;
}

export interface FilteredDocumentsResponse {
  total: number;
  documents: PendingDocument[];
}

export const useFilteredDocuments = (filters?: DocumentFilters) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery<FilteredDocumentsResponse>({
    queryKey: ['documents', tenantScope, filters],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters?.document_type) params.append('document_type', filters.document_type);
      if (filters?.extraction_status) params.append('status', filters.extraction_status);
      if (filters?.vendor_id) params.append('vendor_id', String(filters.vendor_id));
      if (filters?.search) params.append('search', filters.search);
      if (filters?.match_status) params.append('match_status', filters.match_status);
      if (filters?.correlation_id) params.append('correlation_id', filters.correlation_id);
      if (filters?.created_from) params.append('created_from', filters.created_from);
      if (filters?.created_to) params.append('created_to', filters.created_to);
      if (filters?.amount_min !== undefined) params.append('amount_min', String(filters.amount_min));
      if (filters?.amount_max !== undefined) params.append('amount_max', String(filters.amount_max));
      if (filters?.has_issues) params.append('has_issues', 'true');
      if (filters?.erp_connection_id) params.append('erp_connection_id', filters.erp_connection_id);
      if (filters?.sort_by) params.append('sort_by', filters.sort_by);
      if (filters?.sort_order) params.append('sort_order', filters.sort_order);
      if (filters?.skip !== undefined) params.append('skip', String(filters.skip));
      if (filters?.limit !== undefined) params.append('limit', String(filters.limit));

      const { data } = await apiClient.get<FilteredDocumentsResponse>(
        `/documents?${params.toString()}`
      );
      return data;
    },
    staleTime: 5000, // Consider data stale after 5 seconds
    refetchInterval: 10000, // Refetch every 10 seconds
  });
};

const filenameFromDisposition = (value?: string): string => {
  if (!value) {
    return 'ez-ap-documents.zip';
  }

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const plainMatch = value.match(/filename="?([^";]+)"?/i);
  return plainMatch?.[1] || 'ez-ap-documents.zip';
};

export const useExportDocuments = () => {
  const { addNotification } = useNotificationStore();

  return useMutation({
    mutationFn: async (request: DocumentExportRequest) => {
      const response = await apiClient.post<Blob>('/documents/export', request, {
        responseType: 'blob',
      });
      const filename = filenameFromDisposition(response.headers['content-disposition']);
      return { blob: response.data, filename };
    },
    onSuccess: ({ blob, filename }) => {
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);

      addNotification({
        message: 'Document export downloaded',
        type: 'success',
      });
    },
    onError: () => {
      addNotification({
        message: 'Failed to export documents',
        type: 'error',
      });
    },
  });
};

export const useDocumentById = (documentId?: string | null) => {
  const tenantScope = useAuthStore((state) => state.activeTenantId ?? state.currentUser?.active_tenant_id ?? 'no-tenant');

  return useQuery<PendingDocument>({
    queryKey: ['document', tenantScope, documentId],
    queryFn: async () => {
      if (!documentId) throw new Error('Document ID is required');
      const { data } = await apiClient.get<PendingDocument>(`/documents/${documentId}`);
      return data;
    },
    enabled: Boolean(documentId),
    staleTime: 5000,
  });
};

/**
 * Convenience hook for filtering orphaned documents
 */
export const useOrphanedDocuments = () => {
  return useFilteredDocuments({
    extraction_status: 'orphaned',
  });
};

/**
 * Convenience hook for filtering documents by type and status
 */
export const useDocumentsByStatus = (
  documentType: "purchase_order" | 'receipt' | 'invoice' | null,
  extraction_status: string
) => {
  const filters: DocumentFilters = {};
  if (documentType) filters.document_type = documentType;
  if (extraction_status) filters.extraction_status = extraction_status;

  return useFilteredDocuments(filters);
};
